import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { certificationsService } from "api/services/certifications.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import { MEMBER_DEEP_LINK_PARAM_PUBLIC_ID, MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID } from "utils/member-search";

import type { CertificateUpload, CertificateTabKey } from "api/types/certification.types";
import "pages/AdminPage.scss";

// ─────────────────────────────────────────────
// 상수
// ─────────────────────────────────────────────
const TABS: TabItem[] = [
  { key: "all", label: "전체" },
  { key: "pending", label: "미승인" },
  { key: "approved", label: "승인" },
  { key: "rejected", label: "반려" },
];

// 파일 확장자로 이미지 여부 판별
const isImageFile = (url: string) => /\.(jpg|jpeg|png|webp)(\?|$)/i.test(url);

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const CertificationList: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CertificateTabKey>("pending");
  const [items, setItems] = useState<CertificateUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // 승인/거절/삭제 확인 모달
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: "approve" | "reject" | "delete" | null;
    item: CertificateUpload | null;
  }>({ open: false, type: null, item: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // 파일 미리보기 모달
  const [previewModal, setPreviewModal] = useState<{ open: boolean; url: string; name: string }>({
    open: false,
    url: "",
    name: "",
  });

  // ─────────────────────────────────────────
  // 데이터 조회
  // ─────────────────────────────────────────
  const fetchList = async (tab: CertificateTabKey) => {
    setLoading(true);
    try {
      // BE 'rejected' 필터는 없으므로 'pending'으로 받아 rejection_reason 유무로 분리
      const apiStatus = tab === "rejected" ? "pending" : tab;
      const res = await certificationsService.list({ approval_status: apiStatus });
      const list = res.data ?? [];

      const filtered = tab === "pending" ? list.filter((item) => !item.rejection_reason) : tab === "rejected" ? list.filter((item) => !!item.rejection_reason) : list;
      setItems(filtered);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearch("");
    fetchList(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 이름 / 아이디로 클라이언트 필터링 (전체 목록을 한번에 받아오므로 로컬 검색)
  const keyword = search.trim().toLowerCase();
  const visibleItems = keyword
    ? items.filter((item) => {
        const name = (item.certificate_name ?? "").toLowerCase();
        const userName = (item.user_name ?? "").toLowerCase();
        const userId = (item.user_id ?? "").toLowerCase();
        return name.includes(keyword) || userName.includes(keyword) || userId.includes(keyword);
      })
    : items;

  // ─────────────────────────────────────────
  // 승인 / 거절 처리
  // ─────────────────────────────────────────
  const openConfirm = (type: "approve" | "reject" | "delete", item: CertificateUpload) => {
    setRejectionReason("");
    setConfirmModal({ open: true, type, item });
  };

  const closeConfirm = () => setConfirmModal({ open: false, type: null, item: null });

  const handleConfirm = async () => {
    if (!confirmModal.item || !confirmModal.type) return;
    setActionLoading(true);
    try {
      if (confirmModal.type === "delete") {
        await certificationsService.remove(confirmModal.item.id);
      } else {
        await certificationsService.action(confirmModal.item.id, {
          action: confirmModal.type,
          ...(confirmModal.type === "reject" ? { rejection_reason: rejectionReason } : {}),
        });
      }
      closeConfirm();
      fetchList(activeTab);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // 테이블 컬럼 정의
  // ─────────────────────────────────────────
  const isRejectedTab = activeTab === "rejected";

  const baseColumns: Column<CertificateUpload>[] = [
    {
      key: "certificate_file",
      title: "파일",
      width: "72px",
      render: (v, row) => {
        const url = String(v ?? "");
        if (!url) {
          return (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                background: "#F4F4F5",
                border: "1px solid #E4E4E7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              📄
            </div>
          );
        }
        if (isImageFile(url)) {
          return (
            <img
              src={url}
              alt="자격증"
              style={{
                width: 48,
                height: 48,
                objectFit: "cover",
                borderRadius: 6,
                border: "1px solid #E4E4E7",
                cursor: "pointer",
              }}
              onClick={() => setPreviewModal({ open: true, url, name: row.certificate_name })}
            />
          );
        }
        return (
          <button
            onClick={() => setPreviewModal({ open: true, url, name: row.certificate_name })}
            style={{
              background: "none",
              border: "1px solid #E4E4E7",
              borderRadius: 6,
              width: 48,
              height: 48,
              cursor: "pointer",
              fontSize: 20,
            }}
          >
            📄
          </button>
        );
      },
    },
    { key: "certificate_name", title: "자격증 이름" },
    {
      key: "user_name",
      title: "신청자",
    },
    {
      key: "user_id",
      title: "신청자 ID",
      width: "120px",
      render: (v, row) => {
        const userId = (v as string) || "";
        if (!userId) return "-";
        // 값의 **종류를 명시**해서 싣는다. 같은 `?id=` 에 두 종류를 섞으면 소비자가 형식으로
        // 추측해야 하는데, `user_id` 가 UUID 모양인 회원과 그 문자열을 `public_id` 로 가진
        // 다른 회원이 있으면 **엉뚱한 회원의 상세**가 열린다.
        // `public_id` 가 실려 오면 그것으로 딥링크한다 — 소비자가 부분일치 검색을 건너뛰고
        // 상세로 바로 간다. 아직 BE 응답에는 없으므로 optional 이고, 없으면 `user_id` 계약이다.
        // `public_id` 는 외부 노출용 식별자라 URL 에 실어 typed query 로 직행한다.
        // 없는 구 응답의 legacy `user_id`(로그인 아이디)는 **URL 에 싣지 않는다** —
        // 주소창·히스토리·Referer·access log 에 남기 때문이다. navigate state 로 명시해
        // 넘기면 히스토리 entry 에만 붙고 URL 에는 나타나지 않는다.
        const openMember = () => {
          if (row.public_id) {
            navigate(`/user-list?${MEMBER_DEEP_LINK_PARAM_PUBLIC_ID}=${encodeURIComponent(row.public_id)}`);
            return;
          }
          navigate("/user-list", { state: { [MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID]: userId } });
        };
        return (
          <button
            onClick={openMember}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "#6366F1",
              fontWeight: 500,
              fontSize: 13,
              textDecoration: "underline",
            }}
          >
            {userId}
          </button>
        );
      },
    },

    { key: "uploaded_at", title: "신청일", width: "120px", render: (v) => String(v).slice(0, 10) },
    {
      key: "is_approved",
      title: "상태",
      width: "90px",
      render: (v, row) => {
        const approved = v as boolean;
        if (!approved && row.rejection_reason) {
          return <Badge variant="rejected">반려</Badge>;
        }
        return <Badge variant={approved ? "active" : "pending"}>{approved ? "승인" : "미승인"}</Badge>;
      },
    },
  ];

  const rejectionReasonColumn: Column<CertificateUpload> = {
    key: "rejection_reason",
    title: "반려사유",
    render: (v) => {
      const reason = (v as string | null | undefined) ?? "";
      return reason ? <span style={{ fontSize: 13, color: "#3F3F46", whiteSpace: "pre-wrap" }}>{reason}</span> : <span style={{ color: "#A1A1AA" }}>-</span>;
    },
  };

  const actionsColumn: Column<CertificateUpload> = {
    key: "actions",
    title: "관리",
    width: "140px",
    render: (_, row) => {
      // 미승인(승인 X, 반려사유 없음): 승인/거절, 그 외(승인·반려): 삭제
      const isPendingRow = !row.is_approved && !row.rejection_reason;
      if (isPendingRow) {
        return (
          <div style={{ display: "flex", gap: 6 }}>
            <Button size="sm" variant="primary" onClick={() => openConfirm("approve", row)}>
              승인
            </Button>
            <Button size="sm" variant="danger" onClick={() => openConfirm("reject", row)}>
              거절
            </Button>
          </div>
        );
      }
      return (
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={() => openConfirm("delete", row)}>
            삭제
          </Button>
        </div>
      );
    },
  };

  const isAllTab = activeTab === "all";
  const isPendingTab = activeTab === "pending";

  const columns: Column<CertificateUpload>[] = isRejectedTab ? [...baseColumns, rejectionReasonColumn] : isAllTab ? [...baseColumns, rejectionReasonColumn, actionsColumn] : isPendingTab ? [...baseColumns, actionsColumn] : [...baseColumns, actionsColumn]; // 승인 탭: 삭제 버튼

  // ─────────────────────────────────────────
  // 렌더
  // ─────────────────────────────────────────
  return (
    <div className="admin-page">
      <ContentHeader title="자격증 승인 관리" description="강사 및 수강생의 자격증 등록 요청을 승인하거나 거절합니다." />

      <div className="admin-page__card">
        <Tabs items={TABS} activeKey={activeTab} onChange={(k) => setActiveTab(k as CertificateTabKey)} />

        <div className="admin-page__filter">
          <Input placeholder="자격증 이름 / 신청자 이름 / 신청자 ID로 검색" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 300 }} prefix="🔍" />
          {keyword && <span style={{ fontSize: 13, color: "#71717A" }}>{visibleItems.length}건</span>}
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={visibleItems} rowKey="id" />}
      </div>

      {/* 승인/거절 확인 모달 */}
      <Modal
        open={confirmModal.open}
        title={confirmModal.type === "approve" ? "자격증 승인" : confirmModal.type === "delete" ? "자격증 삭제" : "자격증 거절"}
        width="440px"
        onClose={closeConfirm}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="outline" size="sm" onClick={closeConfirm}>
              취소
            </Button>
            <Button variant={confirmModal.type === "approve" ? "primary" : "danger"} size="sm" onClick={handleConfirm} disabled={actionLoading}>
              {actionLoading ? "처리 중..." : confirmModal.type === "approve" ? "승인" : confirmModal.type === "delete" ? "삭제" : "거절"}
            </Button>
          </div>
        }
      >
        <p
          style={{
            fontSize: 14,
            color: "#3F3F46",
            lineHeight: 1.6,
            marginBottom: confirmModal.type === "reject" ? 16 : 0,
          }}
        >
          <strong>"{confirmModal.item?.certificate_name}"</strong> 자격증을 {confirmModal.type === "approve" ? "승인" : confirmModal.type === "delete" ? "삭제" : "거절"}
          하시겠습니까?
          {confirmModal.type === "delete" && <span style={{ display: "block", marginTop: 6, fontSize: 13, color: "#DC2626" }}>삭제된 자격증은 복구할 수 없습니다.</span>}
        </p>
        {confirmModal.type === "reject" && (
          <div>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#3F3F46",
                display: "block",
                marginBottom: 6,
              }}
            >
              거절 사유 <span style={{ color: "#71717A", fontWeight: 400 }}>(선택)</span>
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="거절 사유를 입력하세요."
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}
      </Modal>

      {/* 파일 미리보기 모달 */}
      <Modal open={previewModal.open} title={previewModal.name} width="640px" onClose={() => setPreviewModal({ open: false, url: "", name: "" })}>
        {isImageFile(previewModal.url) ? (
          <img src={previewModal.url} alt={previewModal.name} style={{ width: "100%", borderRadius: 8, display: "block" }} />
        ) : (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <p style={{ fontSize: 14, color: "#71717A", marginBottom: 16 }}>PDF 파일은 새 탭에서 확인하세요.</p>
            <Button variant="primary" size="sm" onClick={() => window.open(previewModal.url, "_blank")}>
              파일 열기
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CertificationList;
